'use client'

import { asset } from '@/lib/assets'
import { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import { Menu, X } from 'lucide-react'
import { getAuthToken, isNormalUserSession, removeAuthToken, verifyToken } from '@/lib/auth'
import { resolvePostLoginRedirectOrDashboardAsync } from '@/lib/postLoginRedirect'
import { landingCtaClick } from '@/lib/ga'
import { scrollToLandingOffset } from './landingSectionScroll'
import { useLandingLogin } from '../LandingLoginProvider'
// Reuses the enterprise landing page's header styles so "/" and "/enterprise"
// share the exact same navbar look and behavior.
import styles from '@/app/enterprise/enterprise-landing.module.css'

const NAV_LINKS = [
  { href: '/#home', label: 'Home', sectionId: 'home' },
  { href: '/about', label: 'About Us', sectionId: null },
  { href: '/#interview-formats', label: 'Interview Formats', sectionId: 'interview-formats' },
  { href: '/#practice-questions', label: 'Practice Questions', sectionId: 'practice-questions' },
  { href: '/#pricing', label: 'Pricing', sectionId: 'pricing' },
  { href: '/#faq', label: "FAQ's", sectionId: 'faq' },
] as const

export function NewUINav({ stickyMenu = false }: { stickyMenu?: boolean } = {}) {
  const router = useRouter()
  const pathname = usePathname()
  const { openLoginModal } = useLandingLogin()
  // A B2C (normal user) session flips "Login" to "Go to app". An enterprise/recruiter
  // session (or no session) leaves this alone — the B2C landing page always shows
  // Login and Enterprise as separate options regardless of enterprise login state.
  const [showNormalGoToApp, setShowNormalGoToApp] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<string>('home')

  useEffect(() => {
    setShowNormalGoToApp(isNormalUserSession())
  }, [])

  useEffect(() => {
    const token = getAuthToken()
    if (!token) {
      setShowNormalGoToApp(false)
      return
    }

    let cancelled = false
    verifyToken().then((result) => {
      if (cancelled) return
      if (!result.valid) {
        removeAuthToken()
        setShowNormalGoToApp(false)
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (pathname !== '/') return

    const sectionIds = NAV_LINKS.map((l) => l.sectionId).filter(
      (id): id is NonNullable<typeof id> => id !== null
    )

    const handleScroll = () => {
      const scrollPos = window.scrollY + 100 // Header offset (80px) + margin

      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const id = sectionIds[i]
        const el = document.getElementById(id)
        if (el) {
          const top = el.offsetTop
          if (scrollPos >= top) {
            setActiveSection(id)
            break
          }
        }
      }
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [pathname])

  const handleGoToApp = async () => {
    const path = await resolvePostLoginRedirectOrDashboardAsync()
    router.push(path)
  }

  const closeMenu = () => setMenuOpen(false)

  const handleLoginClick = () => {
    closeMenu()
    landingCtaClick({ cta_surface: 'navbar', journey: 'login_only' })
    openLoginModal({ loginEntry: 'navbar' })
  }

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    link: (typeof NAV_LINKS)[number]
  ) => {
    closeMenu()
    if (link.sectionId === null) return
    if (pathname !== '/') return

    e.preventDefault()
    setActiveSection(link.sectionId)

    if (link.sectionId === 'home') {
      window.history.replaceState(null, '', '/')
      scrollToLandingOffset(0)
      return
    }

    const el = document.getElementById(link.sectionId)
    if (el) {
      window.history.replaceState(null, '', `/#${link.sectionId}`)
      scrollToLandingOffset(el.getBoundingClientRect().top + window.scrollY - 70)
    }
  }

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    closeMenu()
    if (pathname !== '/') return
    e.preventDefault()
    setActiveSection('home')
    window.history.replaceState(null, '', '/')
    scrollToLandingOffset(0)
  }

  return (
    <>
      <header
        className={`${styles.header} ${menuOpen ? styles.headerMenuOpen : ''} ${menuOpen && stickyMenu ? styles.headerMenuOpenSticky : ''
          }`}
      >
        <div className={styles.headerInner}>
          <div className={styles.headerBrand}>
            <Link href="/" aria-label="meritlens home" onClick={handleHomeClick}>
              <Image
                src={asset("/assets/Icons/meritlens_dark- logo.png")}
                alt="meritlens"
                width={160}
                height={51}
                priority
                style={{ height: '3rem', width: 'auto', display: 'block', marginLeft: '-0.5rem' }}
              />
            </Link>
          </div>

          <nav className={styles.headerNav} aria-label="Primary">
            {NAV_LINKS.map((link) => {
              const isActive =
                link.sectionId === null
                  ? pathname === link.href
                  : pathname === '/' && activeSection === link.sectionId
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  className={isActive ? styles.headerNavActive : ''}
                >
                  {link.label}
                </Link>
              )
            })}
          </nav>

          <div className={styles.headerRight}>
            {showNormalGoToApp ? (
              <button
                type="button"
                className={`${styles.btnPrimary} ${styles.btnHeader} ${styles.headerCta}`}
                onClick={() => { setMenuOpen(false); void handleGoToApp() }}
              >
                Go to Dashboard
              </button>
            ) : (
              <button
                type="button"
                className={`${styles.btnHeaderLogin} ${styles.btnHeaderEnterprise} ${styles.headerCta}`}
                onClick={handleLoginClick}
              >
                Login
              </button>
            )}
            <button
              type="button"
              className={`${styles.btnHeaderLogin} ${styles.btnHeaderEnterprise} ${styles.headerCta}`}
              onClick={() => router.push('/enterprise')}
            >
              Enterprise
            </button>
            <button
              type="button"
              className={styles.menuToggle}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <nav className={`${styles.mobileNav} ${menuOpen ? styles.mobileNavOpen : ''}`} aria-label="Mobile">
          {NAV_LINKS.map((link) => {
            const isActive =
              link.sectionId === null
                ? pathname === link.href
                : pathname === '/' && activeSection === link.sectionId
            return (
              <Link
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link)}
                className={isActive ? styles.headerNavActive : ''}
              >
                {link.label}
              </Link>
            )
          })}

          {showNormalGoToApp ? (
            <button
              type="button"
              className={`${styles.btnPrimary} ${styles.mobileNavCta}`}
              onClick={() => { setMenuOpen(false); void handleGoToApp() }}
            >
              Go to Dashboard
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.btnPrimary} ${styles.mobileNavCta}`}
              onClick={handleLoginClick}
            >
              Login
            </button>
          )}
          <button
            type="button"
            className={`${styles.btnHeaderLogin} ${styles.btnHeaderEnterprise}`}
            onClick={() => { setMenuOpen(false); router.push('/enterprise') }}
          >
            Enterprise
          </button>
        </nav>
      </header>
      {stickyMenu && menuOpen && <div className={styles.headerSpacer} aria-hidden="true" />}
    </>
  )
}
